import React, { useState, useCallback, useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useSearchParams, useNavigate } from "react-router-dom";
import {
  Dashboard,
  AppSelector,
  MetricModal,
  ManualMetricCreator,
  AssignModal,
  GamifyWorkspaceModal,
  MetricSettingsModal,
  BrandedTransition,
} from "../../components/CompanyMetrics";
import { DEFAULT_VISUAL_CONFIG } from "../../components/CompanyMetrics/constants";
import DashboardsPanel from "./dashboards/DashboardsPanel";
import PageHeader from "../../components/PageHeader";
import { UseCompanyWorkspaces, UseCompanySubWorkspaces, UseSelectedWorkspace, UseCompanyColors } from "../../hooks/company";
import { UseCompanyKpis, UseDraftKpis, useCustomKpiCreate, useWorkspaceKpiUpdate, useWorkspaceKpiDisable, buildCompanyScoresByDay, getCompanyMetricChartData } from "../../hooks/kpi";
import { UseUserDetails } from "../../hooks/auth";
import { refreshKpis } from "../../redux/kpi/actions";
import { GET_IMAGE } from "../../helpers/images";
import { GET_ALL_USER_KPIS } from "../../api/kpi";
import {
  KPI_TYPES,
  isKpiGamified,
  getAdvancedGameSettingsFromKpi,
  applyAdvancedGameSettingsToKpiPayload,
  applyAnchorGameSettingsToKpiPayload,
  resolveGamePointsRule,
} from "../../constants/kpi";
import moment from "moment";
import { companyToday } from "../../helpers/companyTime";
import {
  GET_APP_INTEGRATIONS,
  GET_KPI_INTEGRATIONS_BY_DATASET,
  GET_COMPANY_INTEGRATIONS,
} from "../../api/integration";
import { GET_ZAPS, GET_USER as GET_ZAPIER_USER } from "../../api/integrations/zapier";
import { GMB_LOGO_URL } from "../../constants/branding";
import WEBHOOK_LOGO_URL from "../../img/webhookIcon.png";

const METRICS_STORAGE_KEY = "gamemybiz_dashboard_metrics_scoped_v4";
const COLUMNS_STORAGE_KEY = "gamemybiz_dashboard_columns";
const WORKSPACE_STORAGE_KEY = "gamemybiz_active_workspace";

const DEFAULT_BRAND = "#7C3AED";

// hex → {r,g,b}; tolerates #RGB / #RRGGBB / null
function hexToRgb(hex) {
  if (!hex) return { r: 124, g: 58, b: 237 };
  let h = hex.replace("#", "").trim();
  if (h.length === 3) h = h.split("").map((c) => c + c).join("");
  return {
    r: parseInt(h.slice(0, 2), 16),
    g: parseInt(h.slice(2, 4), 16),
    b: parseInt(h.slice(4, 6), 16),
  };
}

function darken(hex, amt = 0.1) {
  const { r, g, b } = hexToRgb(hex);
  const f = 1 - amt;
  return `rgb(${Math.round(r * f)}, ${Math.round(g * f)}, ${Math.round(b * f)})`;
}

function rgba(hex, alpha) {
  const { r, g, b } = hexToRgb(hex);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

/** Same as LeaderBoard/saga: aggregate KPI objects (pts/amt) into a sum object */
function addKpiObjects(kpiSum, plusKpi) {
  if (!plusKpi || typeof plusKpi !== "object") return kpiSum;
  Object.keys(plusKpi).forEach((key) => {
    if (key === "total") return;
    if (kpiSum[key]) {
      kpiSum[key].pts = (kpiSum[key].pts ?? 0) + (plusKpi[key]?.pts ?? 0);
      kpiSum[key].amt = (kpiSum[key].amt ?? 0) + (plusKpi[key]?.amt ?? 0);
    } else {
      kpiSum[key] = { pts: plusKpi[key]?.pts ?? 0, amt: plusKpi[key]?.amt ?? 0 };
    }
    kpiSum.total += plusKpi[key]?.pts ?? 0;
  });
  return kpiSum;
}

/** Build period-keyed company totals from user scores (same logic as LeaderBoard UseTopPlayers + saga) */
function buildCompanyScoresByPeriod(userScores) {
  const day = companyToday();
  const yesterdayStart = companyToday().subtract(1, "days");
  const weekStart = companyToday().startOf("week");
  const monthStart = companyToday().startOf("month");

  const usersKpis = (userScores || []).map((userScore) => {
    const summary = {
      yesterday: { total: 0 },
      today: { total: 0 },
      week: { total: 0 },
      month: { total: 0 },
    };
    (userScore.days || []).forEach((dayData) => {
      const dateRef = moment(dayData.day);
      if (dateRef >= day) addKpiObjects(summary.today, dayData.kpis);
      else if (dateRef >= yesterdayStart) addKpiObjects(summary.yesterday, dayData.kpis);
      if (dateRef >= weekStart) addKpiObjects(summary.week, dayData.kpis);
      if (dateRef >= monthStart) addKpiObjects(summary.month, dayData.kpis);
    });
    return summary;
  });

  const companyByPeriod = {
    yesterday: { total: 0 },
    today: { total: 0 },
    week: { total: 0 },
    month: { total: 0 },
  };
  usersKpis.forEach((u) => {
    addKpiObjects(companyByPeriod.yesterday, u.yesterday);
    addKpiObjects(companyByPeriod.today, u.today);
    addKpiObjects(companyByPeriod.week, u.week);
    addKpiObjects(companyByPeriod.month, u.month);
  });

  return companyByPeriod;
}

/** Map period object { total, [kpiId]: { amt, pts } } to flat { [kpiId]: value } for kpiToMetric */
function periodToScores(periodObj) {
  if (!periodObj || typeof periodObj !== "object") return {};
  return Object.keys(periodObj).reduce((acc, key) => {
    if (key === "total") return acc;
    const v = periodObj[key];
    acc[key] = typeof v === "object" ? (v?.amt ?? v?.pts ?? 0) : v;
    return acc;
  }, {});
}

/** Normalize Redux workspace (e.g. _id, name, image) to Company Metrics shape { id, name, icon } */
function normalizeWorkspaces(workspaces) {
  if (!Array.isArray(workspaces)) return [];
  return workspaces.map((ws) => ({
    id: ws._id || ws.id,
    name: ws.name || "Workspace",
    icon: ws?.image ? GET_IMAGE(ws.image) : "",
  }));
}

/** Build workspaces + subworkspaces for assign/gamify modals: main first, then each parent's subworkspaces with "Parent » Sub" label */
function buildWorkspacesWithSubs(mainWorkspaces, subworkspacesMap) {
  const main = normalizeWorkspaces(mainWorkspaces || []);
  if (!subworkspacesMap || typeof subworkspacesMap !== "object") return main;
  const parentById = (mainWorkspaces || []).reduce((acc, w) => {
    acc[w._id || w.id] = w;
    return acc;
  }, {});
  const subs = [];
  Object.keys(subworkspacesMap).forEach((parentId) => {
    const parent = parentById[parentId];
    const parentName = parent?.name || "Workspace";
    (subworkspacesMap[parentId] || []).forEach((sub) => {
      subs.push({
        id: sub._id || sub.id,
        name: `${parentName} » ${sub.name || "Sub workspace"}`,
        icon: sub?.image ? GET_IMAGE(sub.image) : "",
        isSubWorkspace: true,
      });
    });
  });
  return [...main, ...subs];
}

/**
 * Map company KPI to Company Metrics metric shape.
 * Values come from the same source as LeaderBoard (LiveLeaderboard): user scores aggregated by period (companyScores).
 * Chart data: when companyScoresByDay and selectedMonth are provided, uses chartGranularity ('day' | 'week') for sparklineData.
 */
function kpiToMetric(kpi, companyScores = {}, chartOptions = null) {
  const kpiId = kpi._id || kpi.id;
  const groupId = kpi.groupId || kpi.workspaceId || "GLOBAL";
  const target = kpi.target ?? kpi.model?.defaultTarget ?? 100;
  const name = kpi.name || kpi.title || kpi.model?.type || "KPI";
  const score = companyScores[kpiId];
  const rawValue = typeof score === "number" ? score : (score?.amt ?? score?.total ?? 0);
  const hasRealValue = rawValue != null && Number(rawValue) === rawValue && !Number.isNaN(Number(rawValue));
  const currentValue = hasRealValue ? Math.round(Number(rawValue)) : 0;
  const trend = 0;
  const isGamifyMetric = !kpi.isDraft && isKpiGamified(kpi);
  const advancedSettings = getAdvancedGameSettingsFromKpi(kpi);
  const gameConfig = isGamifyMetric
    ? (() => {
        const { metricAmount, pointsEarned } = resolveGamePointsRule(kpi, advancedSettings);
        const spreadGameConfig = kpi.gameConfig && typeof kpi.gameConfig === "object" ? kpi.gameConfig : {};
        const spreadModelGameConfig = kpi.model?.gameConfig && typeof kpi.model.gameConfig === "object" ? kpi.model.gameConfig : {};
        // GroupKpi schema does not persist gameConfig, so kpi.gameConfig.type is usually missing
        // after a fetch. The persisted source of truth for progressive vs anchor is kpi.metricType.
        let persistedType;
        if (kpi.metricType === "anchor" || kpi.metricType === "progressive") {
          persistedType = kpi.metricType;
        }
        // Anchor settings aren't persisted as gameConfig — they live on the KPI
        // itself (target ÷100 for %, kind, optional.benchmarkPoint…). Without
        // this rehydration the modal reopened on stale defaults, and re-saving
        // a "%" anchor divided the target by 100 again (77 → 0.77).
        let anchorOverrides = null;
        if (kpi.metricType === "anchor") {
          const kindToAnchorUnit = { "%": "percent", $: "currency", "#": "number" };
          const anchorUnit = kindToAnchorUnit[kpi.kind] || "percent";
          const rawTarget = typeof kpi.target === "number" ? kpi.target : Number(kpi.target ?? 0);
          const opt = kpi.optional || {};
          anchorOverrides = {
            anchorValue: anchorUnit === "percent" ? Math.round(rawTarget * 100) : rawTarget,
            anchorUnit,
            ...(opt.benchmarkPoint != null && { benchmarkPoints: Number(opt.benchmarkPoint) }),
            ...(opt.aboveBenchmark != null && { pointsPerUnitAbove: Number(opt.aboveBenchmark) }),
            ...(opt.belowBenchmark != null && { pointsPerUnitBelow: Number(opt.belowBenchmark) }),
          };
        }
        return {
          status: "live",
          dailyTarget: typeof target === "number" ? target : 100,
          ...spreadModelGameConfig,
          ...spreadGameConfig,
          ...(anchorOverrides || {}),
          ...(persistedType && { type: persistedType }),
          pointsRule: { metricAmount, pointsEarned },
          guideText: typeof kpi.guideText === "string" ? kpi.guideText : (spreadGameConfig.guideText ?? ""),
          dailyGoalText: typeof kpi.dailyGoalText === "string" ? kpi.dailyGoalText : (spreadGameConfig.dailyGoalText ?? ""),
          monitorMode: advancedSettings.monitorMode,
          scorekeeperOnly: advancedSettings.scorekeeperOnly,
          resetFrequency: advancedSettings.resetFrequency,
          compositeMetric: advancedSettings.compositeMetric,
          valueType: advancedSettings.valueType,
          effectType: advancedSettings.effectType,
          ...(advancedSettings.compositeConfig && { compositeConfig: advancedSettings.compositeConfig }),
        };
      })()
    : undefined;
  // Chart data view: only kpi.chartGranularity; if no value use "day" (per metric)
  let appearanceType = null;
  const chartGranularity = (kpi.chartGranularity === "day" || kpi.chartGranularity === "week") ? kpi.chartGranularity : "day";
  const appearanceObj = (kpi.appearance && typeof kpi.appearance === "object") ? kpi.appearance : {};
  if (typeof kpi.appearance === "string") {
    appearanceType = kpi.appearance;
  } else if (typeof appearanceObj.type === "string") {
    appearanceType = appearanceObj.type;
  } else if (kpi.visualConfig && typeof kpi.visualConfig === "object" && typeof kpi.visualConfig.type === "string") {
    appearanceType = kpi.visualConfig.type;
  }
  const visualConfig = {
    ...DEFAULT_VISUAL_CONFIG,
    type: appearanceType ?? DEFAULT_VISUAL_CONFIG.type,
    chartGranularity,
    ...(appearanceObj.primaryColor != null && { primaryColor: appearanceObj.primaryColor }),
    ...(appearanceObj.gradientEnabled != null && { gradientEnabled: appearanceObj.gradientEnabled }),
    ...(appearanceObj.smoothLine != null && { smoothLine: appearanceObj.smoothLine }),
    ...(appearanceObj.chartOpacity != null && { chartOpacity: appearanceObj.chartOpacity }),
  };
  const typeForCard = appearanceType || visualConfig.type;

  // Chart data: day (all days of month) or week (4 aggregated points)
  let sparklineData = [{ timestamp: "0", value: 0 }, { timestamp: "1", value: currentValue }];
  if (chartOptions?.companyScoresByDay && chartOptions?.selectedMonth) {
    sparklineData = getCompanyMetricChartData(
      chartOptions.companyScoresByDay,
      kpiId,
      chartOptions.selectedMonth,
      chartGranularity
    );
    if (!sparklineData.length) sparklineData = [{ timestamp: "1", value: currentValue }];
  }

  return {
    id: kpiId,
    dashboardId: `kpi-${kpiId}-${groupId}`,
    workspaceId: "GLOBAL",
    ownerWorkspaceId: groupId,
    name: typeof name === "string" ? name : "Metric",
    description: "",
    unit: "number",
    currentValue,
    trend,
    sparklineData,
    appId: kpi.source || "manual",
    deploymentStatus: "active",
    visualConfig,
    appearance: typeof kpi.appearance === "string" ? kpi.appearance : typeForCard,
    _source: "companyKpi",
    ...(gameConfig && { gameConfig }),
    ...(kpi.optional && { optional: kpi.optional }),
    ...(kpi.parameters && { parameters: kpi.parameters }),
  };
}

/** Map ManualMetricCreator unit to KPI kind (same as ExploreCustomMetricsTypes) */
function unitToKind(unit) {
  if (unit === "currency") return "$";
  if (unit === "percent") return "%";
  return "#";
}

function CompanyMetricsPage() {
  const { primaryColor: brandPrimary } = UseCompanyColors() || {};
  const themeVars = useMemo(() => {
    const base = brandPrimary || DEFAULT_BRAND;
    return {
      "--gmb-primary": base,
      "--gmb-primary-hover": darken(base, 0.1),
      "--gmb-primary-soft": rgba(base, 0.08),
      "--gmb-primary-tint": rgba(base, 0.14),
      "--gmb-primary-ring": rgba(base, 0.16),
      fontFamily: '"DM Sans", system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    };
  }, [brandPrimary]);

  const selectedWorkspace = UseSelectedWorkspace();
  const reduxWorkspaces = UseCompanyWorkspaces();
  const subworkspacesMap = UseCompanySubWorkspaces();
  const companyWorkspaces = useMemo(() => normalizeWorkspaces(reduxWorkspaces), [reduxWorkspaces]);
  const workspacesForAssign = useMemo(
    () => buildWorkspacesWithSubs(reduxWorkspaces, subworkspacesMap),
    [reduxWorkspaces, subworkspacesMap]
  );
  const dispatch = useDispatch();
  const triggerKpiRefresh = useCallback(() => dispatch(refreshKpis()), [dispatch]);
  const { companyId } = UseUserDetails() || {};
  const { createCustomKpi } = useCustomKpiCreate();
  const { updateWorkspaceKpi, updateKpiGuideText, updateKpiDailyGoalText } = useWorkspaceKpiUpdate();
  const { disableWorkspaceKpi } = useWorkspaceKpiDisable();

  const companyKpisRedux = UseCompanyKpis();
  const draftKpisRedux = UseDraftKpis();
  const kpiLoading = useSelector((state) => state.kpiReducer.isLoading);

  const [allMetrics, setAllMetrics] = useState([]);
  const [integrationApps, setIntegrationApps] = useState([]);
  const [allAppsRaw, setAllAppsRaw] = useState([]);
  const [integrationsObj, setIntegrationsObj] = useState(null);
  const [appsLoading, setAppsLoading] = useState(true);
  const [activeWorkspace, setActiveWorkspace] = useState(null);
  const [selectedApp, setSelectedApp] = useState(null);
  const [columnCount, setColumnCount] = useState(3);
  const [assigningMetric, setAssigningMetric] = useState(null);
  const [gamifyForWorkspaceMetric, setGamifyForWorkspaceMetric] = useState(null);
  const [settingsMetric, setSettingsMetric] = useState(null);
  const [settingsTab, setSettingsTab] = useState("appearance");
  const [isSwitchingContext, setIsSwitchingContext] = useState(false);
  const [toast, setToast] = useState(null);
  // Optimistic appearance overrides keyed by kpi id — lets a saved look show on
  // the card instantly instead of waiting for the server round-trip + refetch.
  const [appearanceOverrides, setAppearanceOverrides] = useState({});
  const [view, setView] = useState("COMPANY_DASHBOARD"); // 'COMPANY_DASHBOARD' | 'APP_SELECTION' | 'MANUAL_CREATOR'
  const [togglePeriod, setTogglePeriod] = useState(3); // 0 Yesterday, 1 Today, 2 This Week, 3 This Month (same as LeaderBoard)
  const [companyScoresByPeriod, setCompanyScoresByPeriod] = useState(null); // { yesterday, today, week, month } like LeaderBoard
  const [companyScoresByDay, setCompanyScoresByDay] = useState(null); // per-day totals for current month (chart Day/Week view)

  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();

  // Active top-level view — driven by ?tab so the toggle can live in the shared
  // TopBar and the view stays deep-linkable. Defaults to metrics.
  const topTab = searchParams.get("tab") === "dashboards" ? "dashboards" : "metrics";

  // When arriving from workspace metrics "Create metric", open the equivalent create flow
  useEffect(() => {
    const create = searchParams.get("create");
    if (create === "integrated") {
      setView("APP_SELECTION");
      setSearchParams({}, { replace: true });
    } else if (create === "manual") {
      setView("MANUAL_CREATOR");
      setSearchParams({}, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  const role = "admin";
  const contextMode = "GLOBAL";

  useEffect(() => {
    const savedMetrics = localStorage.getItem(METRICS_STORAGE_KEY);
    if (savedMetrics) {
      try {
        setAllMetrics(JSON.parse(savedMetrics));
      } catch (e) {
        console.error("Failed to parse saved metrics", e);
      }
    }
    const savedColumns = localStorage.getItem(COLUMNS_STORAGE_KEY);
    if (savedColumns) {
      const cols = parseInt(savedColumns, 10);
      if (!Number.isNaN(cols)) setColumnCount(cols);
    }
  }, []);

  useEffect(() => {
    if (companyWorkspaces.length === 0) return;
    const savedWS = localStorage.getItem(WORKSPACE_STORAGE_KEY);
    const found = savedWS ? companyWorkspaces.find((w) => w.id === savedWS) : null;
    setActiveWorkspace((prev) => {
      if (found) return found;
      if (prev && companyWorkspaces.some((w) => w.id === prev.id)) return prev;
      return companyWorkspaces[0] || null;
    });
  }, [companyWorkspaces]);

  useEffect(() => {
    if (toast) {
      const timer = setTimeout(() => setToast(null), 3000);
      return () => clearTimeout(timer);
    }
  }, [toast]);

  // KPIs come from Redux (same as workspace metrics) via UseCompanyKpis + UseDraftKpis; trigger saga so data is loaded
  useEffect(() => {
    if (companyId && companyWorkspaces?.length > 0 && triggerKpiRefresh) triggerKpiRefresh();
  }, [companyId, companyWorkspaces?.length, triggerKpiRefresh]);

  // Fetch user scores per workspace and compute company totals by period (same as LeaderBoard).
  // LeaderBoard uses UseTopPlayers(period) which reads item[period] from Redux usersKpis - no refetch on period change.
  // We do the same: fetch once, build yesterday/today/week/month client-side, then filter = pick period.
  useEffect(() => {
    if (!companyId || !companyWorkspaces?.length) {
      setCompanyScoresByPeriod(null);
      setCompanyScoresByDay(null);
      return;
    }
    let cancelled = false;
    Promise.all(
      companyWorkspaces.map((ws) =>
        GET_ALL_USER_KPIS(companyId, ws.id).then((res) => ({ scores: res.data?.scores ?? [], users: res.data?.users ?? [] }))
      )
    )
      .then((workspaceData) => {
        const allScores = workspaceData.flatMap((d) => d.scores);
        const userScores = [];
        // Shared/company-wide scores (e.g. Group-owned rollups like Total
        // Revenue) can come back from more than one workspace's fetch when
        // the metric is visible from several workspaces - dedupe by owner
        // so the same underlying score doesn't get summed multiple times.
        const sharedScoresByOwner = new Map();
        allScores.forEach((score) => {
          if (score.scope === "User") {
            userScores.push({ ...score, days: score.days ?? [] });
          } else {
            const key = score.ownerId ?? score.userId ?? score._id;
            if (!sharedScoresByOwner.has(key)) sharedScoresByOwner.set(key, score.days ?? []);
          }
        });
        const sharedScores = [...sharedScoresByOwner.values()].flat();
        if (sharedScores.length > 0) {
          // Shared scores belong to the company as a whole, not to any one
          // user - add them once via a single synthetic entry instead of
          // attaching to every user, which previously multiplied the total
          // by the number of company users.
          userScores.push({ ownerId: "__shared__", days: sharedScores });
        }
        const byPeriod = buildCompanyScoresByPeriod(userScores);
        const byDay = buildCompanyScoresByDay(userScores, companyToday().startOf("month"));
        if (!cancelled) {
          setCompanyScoresByPeriod(byPeriod);
          setCompanyScoresByDay(byDay);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setCompanyScoresByPeriod(null);
          setCompanyScoresByDay(null);
        }
      });
    return () => { cancelled = true; };
  }, [companyId, companyWorkspaces]);

  // Derive companyScores from selected period (same as LeaderBoard: filter = pick period, no refetch)
  const companyScores = useMemo(() => {
    const periodApiMap = ["yesterday", "today", "week", "month"];
    const periodParam = periodApiMap[togglePeriod] ?? "month";
    if (!companyScoresByPeriod || !companyScoresByPeriod[periodParam]) return {};
    return periodToScores(companyScoresByPeriod[periodParam]);
  }, [companyScoresByPeriod, togglePeriod]);

  // Fetch integration apps for logo resolution (same source as workspace-kpi: GET_APP_INTEGRATIONS + manual)
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const { data } = await GET_APP_INTEGRATIONS();
        const apps = Array.isArray(data) ? [...data] : [];
        const manualEntry = {
          name: "Game My Biz",
          code: "manual",
          logo: GMB_LOGO_URL,
          isManual: true,
        };
        if (!apps.some((a) => (a.code || a.id) === "manual")) {
          apps.push(manualEntry);
        }
        if (!cancelled) setIntegrationApps(apps);
      } catch {
        if (!cancelled) setIntegrationApps([{ code: "manual", logo: GMB_LOGO_URL, name: "Game My Biz", isManual: true }]);
      }
    };
    load();
    return () => { cancelled = true; };
  }, []);

  // Fetch connected applications (same data as WorkspaceKpi ConnectedApplications)
  const addZapierKpis = useCallback(async (appsData) => {
    try {
      const [zapTemplatesResponse, zapUserResponse] = await Promise.all([
        GET_ZAPS(),
        GET_ZAPIER_USER().catch(() => ({ data: null })),
      ]);
      const zapTemplates = zapTemplatesResponse?.data ?? [];
      const zapUser = zapUserResponse?.data ?? null;
      const isZapierConnected = zapUser && !zapUser.tokenExpired;
      zapTemplates.forEach((z) => {
        const idx = appsData.findIndex((app) => app.name === z.name);
        z.isConnected = !!isZapierConnected;
        const zaps = (z.zaps ?? []).map((zap) => ({
          ...zap,
          id: z.serviceId,
          integrationId: z._id,
          description: (zap.description || "").replace("Game My Biz", ""),
          isZapier: true,
        }));
        if (idx >= 0) {
          appsData[idx].zaps = zaps;
        } else {
          appsData.push({ ...z, zaps });
        }
      });
      return appsData;
    } catch {
      return appsData;
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    const loadConnectedApps = async () => {
      setAppsLoading(true);
      try {
        const [appsResponse, integResponse, companyIntegrationsResponse] = await Promise.all([
          GET_APP_INTEGRATIONS(),
          GET_KPI_INTEGRATIONS_BY_DATASET(),
          GET_COMPANY_INTEGRATIONS(),
        ]);
        let appsData = Array.isArray(appsResponse?.data) ? [...appsResponse.data] : [];
        const integData = integResponse?.data ?? {};
        const companyIntegrations = Array.isArray(companyIntegrationsResponse?.data) ? companyIntegrationsResponse.data : [];

        for (let j = 0; j < appsData.length; j += 1) {
          const { code, serviceId } = appsData[j];
          const idx = companyIntegrations.findIndex((app) =>
            serviceId ? app.serviceId === serviceId : app.code === code
          );
          if (idx >= 0) {
            appsData[j].isConnected = true;
            appsData[j].paramKeys = { ...companyIntegrations[idx].parameters };
          } else {
            appsData[j].isConnected = false;
          }
        }
        appsData = await addZapierKpis(appsData);

        if (!appsData.some((a) => (a.code || a.id) === "manual")) {
          appsData.push({
            name: "Game My Biz",
            code: "manual",
            logo: GMB_LOGO_URL,
            description: "Custom-built metrics for your business",
            isConnected: true,
            isManual: true,
            models: [],
          });
        }
        if (!appsData.some((a) => (a.code || a.id) === "webhook")) {
          appsData.push({
            name: "Webhook",
            code: "webhook",
            logo: WEBHOOK_LOGO_URL,
            description: "All payloads available",
            isConnected: true,
            isWebhook: true,
            models: [],
          });
        }

        const sortedApps = appsData.sort((app1, app2) => {
          if (app1.isConnected && !app2.isConnected) return -1;
          if (!app1.isConnected && app2.isConnected) return 1;
          if (!app1.isZapier && app2.isZapier) return -1;
          if (app1.isZapier && !app2.isZapier) return 1;
          if (!app1.isManual && app2.isManual) return -1;
          if (app1.isManual && !app2.isManual) return 1;
          return (app1.name || "").localeCompare(app2.name || "");
        });

        if (!cancelled) {
          setAllAppsRaw(sortedApps);
          setIntegrationsObj(integData);
        }
      } catch {
        if (!cancelled) {
          setAllAppsRaw([]);
          setIntegrationsObj({});
        }
      } finally {
        if (!cancelled) setAppsLoading(false);
      }
    };
    loadConnectedApps();
    return () => { cancelled = true; };
  }, [addZapierKpis]);

  // Build apps with metrics (same logic as ConnectedApplications)
  const appsWithMetrics = useMemo(() => {
    if (!allAppsRaw?.length || !integrationsObj) return [];
    return allAppsRaw.map((app) => {
      const officialIntegrations = app.dataSets
        ? app.dataSets.flatMap(
            (d) =>
              (integrationsObj[d] ?? []).map(
                ({ sourceId, type, description, _id }) => ({
                  type,
                  description,
                  app: app.name,
                  logo: app.logo,
                  code: app.code,
                  sourceId,
                  integrationId: app._id,
                  ...(_id != null && { kpiModelId: _id }),
                  isConnected: app.isConnected,
                })
              ) ?? []
          )
        : [];
      const allIntegrations = [
        ...officialIntegrations,
        ...(app.models?.map((m) => ({
          ...m,
          app: app.name,
          logo: app.logo,
          code: app.code,
          integrationId: app._id,
          ...(m._id != null && { kpiModelId: m._id }),
          isManual: true,
          isConnected: true,
        })) ?? []),
        ...(app.zaps?.map((z) => ({
          ...z,
          app: app.name,
          logo: app.logo,
          code: app.code,
          integrationId: app._id,
          isConnected: app.isConnected,
        })) ?? []),
      ];
      return { ...app, metrics: allIntegrations };
    });
  }, [allAppsRaw, integrationsObj]);

  // Flattened list of all company KPIs from Redux (same source as workspace; not filtered by workspaceId)
  const allCompanyKpisRaw = useMemo(() => {
    const byWorkspace = companyKpisRedux && typeof companyKpisRedux === "object" ? companyKpisRedux : {};
    // A metric linked to multiple workspaces (GroupKpi.linkedGroupIds) is the
    // SAME document appearing in more than one workspace's bucket by design
    // (see redux/kpi/saga.js and hooks/kpi.js) — flattening every bucket
    // together would otherwise produce one duplicate card per linked
    // workspace here. Dedupe by _id so the catalog shows each metric once.
    const seen = new Set();
    const nonDraft = Object.values(byWorkspace).flat().filter((k) => {
      if (k.disabled === true) return false;
      const id = k._id || k.id;
      if (id) {
        if (seen.has(id)) return false;
        seen.add(id);
      }
      return true;
    });
    const drafts = Array.isArray(draftKpisRedux) ? draftKpisRedux : [];
    return [...nonDraft, ...drafts];
  }, [companyKpisRedux, draftKpisRedux]);

  // Same source as workspace metrics (UseCompanyKpis + UseDraftKpis); list is all company KPIs, not filtered by workspaceId
  const kpiMetrics = useMemo(() => {
    const scores = companyScores;
    const normalized = allCompanyKpisRaw.map((k) => ({
      ...k,
      model: k.model || { defaultTarget: k.defaultTarget ?? k.target, kind: k.kind, type: k.type },
    }));
    const selectedMonth = companyToday().startOf("month");
    const chartOptions = companyScoresByDay
      ? { companyScoresByDay, selectedMonth }
      : null;
    return normalized.map((kpi) => {
      const m = kpiToMetric(kpi, scores, chartOptions);
      const ov = appearanceOverrides[m.id];
      if (ov) {
        return {
          ...m,
          visualConfig: { ...m.visualConfig, ...ov },
          appearance: typeof ov.type === "string" ? ov.type : m.appearance,
        };
      }
      return m;
    });
  }, [allCompanyKpisRaw, companyScores, companyScoresByDay, appearanceOverrides]);

  const handleUpdateColumns = useCallback((cols) => {
    setColumnCount(cols);
    localStorage.setItem(COLUMNS_STORAGE_KEY, cols.toString());
  }, []);

  const persistMetrics = (updated) => {
    setAllMetrics(updated);
    localStorage.setItem(METRICS_STORAGE_KEY, JSON.stringify(updated));
  };

  const globalMetrics = useMemo(() => allMetrics.filter((m) => m.workspaceId === m.ownerWorkspaceId), [allMetrics]);
  const combinedMetrics = useMemo(() => [...kpiMetrics, ...globalMetrics], [kpiMetrics, globalMetrics]);
  const combinedAllMetrics = useMemo(() => [...kpiMetrics, ...allMetrics], [kpiMetrics, allMetrics]);

  /** Remove (disable) an app metric from company dashboard. Same as MetricItem handleDelete. */
  const handleRemoveAppMetric = useCallback(
    async (kpiId) => {
      try {
        await disableWorkspaceKpi(kpiId);
        triggerKpiRefresh();
        setToast({ message: "Metric removed from dashboard.", type: "success" });
      } catch (err) {
        setToast({
          message: err?.message || "Failed to remove metric. Please try again.",
          type: "error",
        });
      }
    },
    [disableWorkspaceKpi, triggerKpiRefresh]
  );

  /**
   * Save app/integration metrics from "Connect a source" as display metrics (same flow as MetricItem/CatalogMetricCard).
   * Includes metric name and uses updateWorkspaceKpi with companyId so they appear on company dashboard.
   */
  const handleConfirmMetric = useCallback(
    async (metricDef) => {
      const selectedGroupId = selectedWorkspace?._id;
      const isIntegration = metricDef?._source === "integration" && metricDef?._integration;
      if (isIntegration && companyId) {
        const { _integration } = metricDef;
        const metricName = metricDef.name || "Unnamed Metric";
        const integrationMeta = _integration?.isZapier
          ? { id: metricDef.id, code: metricDef.appId, status: "disconnected" }
          : null;
        const displayMetricPayload = {
          target: 1,
          pointScale: 1,
          metricType: KPI_TYPES[0], // "display"
          direction: 1,
        };
        const kpiPayload = {
          name: metricName,
          type: _integration?.type || metricName,
          ...displayMetricPayload,
          ...(_integration?.kpiModelId != null && { kpiModelId: _integration.kpiModelId }),
          ...(_integration?.sourceId != null && { sourceId: _integration.sourceId }),
          ...(_integration?.integrationId != null && { integrationId: _integration.integrationId }),
          ...(integrationMeta && { integrationMeta }),
          isDraft: true,
          isSaved: true,
          groupId: selectedGroupId,
        };
        try {
          const data = await updateWorkspaceKpi(companyId, kpiPayload);
          triggerKpiRefresh();
          setSelectedApp(null);
          setView("COMPANY_DASHBOARD");
          setToast({ message: "Metric saved successfully.", type: "success" });
          if (_integration?.isZapier && metricDef?.id) {
            navigate(`/zapier-setup?id=${data?._id}`);
          }
        } catch (err) {
          setToast({
            message: err?.message || "Failed to save metric. Please try again.",
            type: "error",
          });
        }
        return;
      }
      const newMetric = {
        ...metricDef,
        dashboardId: `${metricDef.id}-${Date.now()}`,
        workspaceId: "GLOBAL",
        ownerWorkspaceId: "GLOBAL",
        deploymentStatus: "active",
      };
      persistMetrics([...allMetrics, newMetric]);
    },
    [allMetrics, companyId, selectedWorkspace, updateWorkspaceKpi, triggerKpiRefresh, navigate]
  );

  const handleConfirmManualMetric = useCallback(
    async (metricDef, manualConfig) => {
      if (!companyId) {
        setToast({ message: "Company context is required to save the metric.", type: "error" });
        return;
      }
      // Build kpiData in same shape as ExploreCustomMetricsTypes (progressive metric) so server accepts it
      const kind = unitToKind(metricDef.unit || "number");
      const kpiData = {
        name: metricDef.name || "Unnamed Metric",
        kind,
        direction: 1,
        target: 100,
        pointScale: 1,
        pointValue: 10,
        groupId: companyId,
        metricType: KPI_TYPES[0],
        webhook: false,
        isDraft: true,
        isSaved: true,
        source: "manual",
        advanced: false,
        manualConfig: { ...manualConfig },
        ...(manualConfig?.requiredInputs && { requiredInputs: manualConfig.requiredInputs }),
      };
      try {
        await createCustomKpi(kpiData);
        triggerKpiRefresh();
        setView("COMPANY_DASHBOARD");
        setToast({ message: "Manual metric saved successfully.", type: "success" });
      } catch (err) {
        setToast({
          message: err?.message || "Failed to save manual metric. Please try again.",
          type: "error",
        });
      }
    },
    [companyId, createCustomKpi, triggerKpiRefresh]
  );

  const handleRemoveMetric = useCallback(
    async (dashboardId) => {
      // Company KPIs aren't in localStorage allMetrics; they're sourced from the server.
      // For those, hit the disable endpoint so removal actually persists.
      const target = combinedAllMetrics.find((m) => m.dashboardId === dashboardId);
      if (target?._source === "companyKpi" && target.id) {
        try {
          await disableWorkspaceKpi(target.id);
          triggerKpiRefresh();
          setToast({ message: "Metric removed from dashboard.", type: "success" });
        } catch (err) {
          setToast({
            message: err?.message || "Failed to remove metric. Please try again.",
            type: "error",
          });
        }
        return;
      }
      persistMetrics(allMetrics.filter((m) => m.dashboardId !== dashboardId));
    },
    [allMetrics, combinedAllMetrics, disableWorkspaceKpi, triggerKpiRefresh]
  );

  const handleUpdateMetric = useCallback(
    (dashboardId, visual, game) => {
      persistMetrics(
        allMetrics.map((m) => (m.dashboardId === dashboardId ? { ...m, visualConfig: visual, gameConfig: game } : m))
      );
    },
    [allMetrics]
  );

  /** Save appearance/gamification for a metric. Company KPIs are persisted via updateWorkspaceKpi (same as ExploreCustomMetricsTypes); other metrics via local state. */
  const handleSaveMetricFromSettings = useCallback(
    async (metricId, visual, game, shouldClose = true) => {
      const metric = settingsMetric;
      if (!metric) {
        if (shouldClose) setSettingsMetric(null);
        return;
      }
      if (metric._source === "companyKpi") {
        const rawKpi = allCompanyKpisRaw.find((k) => (k._id || k.id) === metric.id);
        const groupId = metric.ownerWorkspaceId;
        if (!rawKpi || !groupId) {
          setToast({ message: "Could not find KPI to update on the server.", type: "error" });
          if (shouldClose) setSettingsMetric(null);
          return;
        }
        // The server's buildCustomMetricData reads composite config from
        // TOP-LEVEL dataSourceOne/calculationMethod/dataSourceTwo — not from
        // inside gameConfig — matching how the AI creator flow already sends
        // it (see handleConfirmAiMetric below). Without this mapping the
        // composite toggle/pickers save nothing (optional stays {}), and any
        // computed value shown before a refresh was purely a local optimistic
        // preview that was never actually persisted.
        const hasComposite = !!(
          game?.compositeMetric &&
          game?.compositeConfig?.metricAId &&
          game?.compositeConfig?.metricBId
        );
        const kpiPayload = {
          ...rawKpi,
          appearance: {
            type: typeof visual?.type === "string" ? visual.type : DEFAULT_VISUAL_CONFIG.type,
            chartGranularity: visual?.chartGranularity === "week" ? "week" : "day",
            primaryColor: visual?.primaryColor ?? DEFAULT_VISUAL_CONFIG.primaryColor,
            gradientEnabled: visual?.gradientEnabled ?? DEFAULT_VISUAL_CONFIG.gradientEnabled,
            smoothLine: visual?.smoothLine ?? DEFAULT_VISUAL_CONFIG.smoothLine,
            chartOpacity: visual?.chartOpacity ?? DEFAULT_VISUAL_CONFIG.chartOpacity,
          },
          gameConfig: game,
          ...(hasComposite && {
            dataSourceOne: game.compositeConfig.metricAId,
            calculationMethod: game.compositeConfig.operator,
            dataSourceTwo: game.compositeConfig.metricBId,
          }),
          ...(game && typeof game.guideText === "string" ? { guideText: game.guideText.trim() } : {}),
          ...(game && game.status === "live" ? { isDraft: false, isSaved: false } : {}),
        };
        // Optimistic: reflect the new look on the card and confirm right away,
        // then persist in the background. Reconciles on the refetch; reverts if
        // the server rejects it.
        if (visual && typeof visual === "object") {
          setAppearanceOverrides((prev) => ({ ...prev, [metric.id]: visual }));
        }
        setToast({ message: "Appearance saved successfully.", type: "success" });
        if (shouldClose) setSettingsMetric(null);
        try {
          await updateWorkspaceKpi(groupId, kpiPayload);
          triggerKpiRefresh();
        } catch (err) {
          setAppearanceOverrides((prev) => {
            const next = { ...prev };
            delete next[metric.id];
            return next;
          });
          setToast({
            message: err?.message || "Could not save appearance settings.",
            type: "error",
          });
        }
        return;
      }
      handleUpdateMetric(metricId, visual, game);
      if (shouldClose) setSettingsMetric(null);
    },
    [settingsMetric, allCompanyKpisRaw, updateWorkspaceKpi, triggerKpiRefresh, handleUpdateMetric]
  );

  /**
   * Assign a company metric template to one or more workspaces. Each
   * workspace gets its own independent GroupKpi document — a fresh
   * generated _id forces the server's upsert-by-_id update (updateKpi) to
   * CREATE a new record instead of matching/overwriting the template (or
   * any other existing metric), so the template itself is never touched and
   * copies never affect each other after this point.
   */
  /**
   * Assign a company metric template to one or more workspaces. This links
   * the SAME document (same _id/kpiId) onto every selected board — it does
   * NOT create copies. That matters because metrics whose scores are written
   * by a single company-wide process keyed on one kpiId (e.g. the HCP hours
   * sync, which writes every technician's hours under one shared kpiId
   * regardless of their workspace) only render correctly when every board
   * that should show them points at that same kpiId. A copy-based approach
   * would silently never receive that data.
   *
   * Editing (target, appearance, gamification, etc.) is intentionally only
   * exposed from the Company Metrics page (see Dashboard.jsx isGlobalView) —
   * since every linked board shares one document, editing it from a random
   * workspace board would be confusing about which board "owns" the change.
   */
  const handleAssignMetric = useCallback(
    async (metric, targetWorkspaceIds) => {
      const targetIds = Array.isArray(targetWorkspaceIds) ? targetWorkspaceIds : [targetWorkspaceIds];
      if (!targetIds.length || targetIds.some((id) => !id)) {
        setToast({ message: "Please select at least one workspace.", type: "error" });
        return;
      }
      const rawKpi = allCompanyKpisRaw.find((k) => (k._id || k.id) === metric.id);
      if (!rawKpi) {
        setToast({ message: "Could not find metric data to update.", type: "error" });
        setAssigningMetric(null);
        return;
      }
      const kpiId = rawKpi._id || rawKpi.id;

      try {
        // Same _id back to the server (no groupId override) — this appends
        // to linkedGroupIds on the existing document rather than creating or
        // moving anything.
        await updateWorkspaceKpi(kpiId, { ...rawKpi, linkedGroupIdsToAdd: targetIds });
        triggerKpiRefresh();
        const otherMetrics = allMetrics.filter((m) => !(m.id === metric.id && m.workspaceId !== "GLOBAL"));
        persistMetrics(otherMetrics);
        setAssigningMetric(null);
        setToast({
          message: targetIds.length > 1
            ? `Metric successfully assigned to ${targetIds.length} workspaces.`
            : "Metric successfully assigned to workspace.",
          type: "success",
        });
      } catch (err) {
        setToast({
          message: err?.message || "Failed to assign metric. Please try again.",
          type: "error",
        });
        setAssigningMetric(null);
      }
    },
    [allMetrics, allCompanyKpisRaw, updateWorkspaceKpi, triggerKpiRefresh]
  );

  /**
   * Gamify for workspace: updates the KPI on the server using the same payload shape as
   * ExploreCustomMetricsTypes (kpiRaw = base KPI + update fields + groupId + requiredInputs),
   * then disables the source metric. Closes the modal and shows success toast.
   */
  const handleGamifyForWorkspace = useCallback(
    async (metric, targetWsId, gameConfig) => {
      const rawKpi = allCompanyKpisRaw.find((k) => (k._id || k.id) === metric.id);
      if (!rawKpi) {
        setToast({ message: "Could not find metric data to update.", type: "error" });
        setGamifyForWorkspaceMetric(null);
        return;
      }
      setGamifyForWorkspaceMetric(null);
      setIsSwitchingContext(true);

      // Use the draft's own _id so each metric becomes its own workspace entry
      const baseKpi = rawKpi;
      const model = rawKpi.model || {};

      // Build update fields in same shape as ExploreCustomMetricsTypes (progressive metric update)
      let kind = rawKpi.kind || model?.kind || "#";
      if (gameConfig?.valueType === "monetary") kind = "$";
      else if (gameConfig?.valueType === "item") kind = "#";
      const target =
        gameConfig != null && typeof gameConfig.dailyTarget === "number"
          ? gameConfig.dailyTarget
          : (rawKpi.target ?? model?.defaultTarget ?? 100);
      const pointsRule =
        gameConfig?.pointsRule && typeof gameConfig.pointsRule.metricAmount === "number"
          ? gameConfig.pointsRule
          : (rawKpi.pointsRule || model?.pointsRule || { metricAmount: 1, pointsEarned: 10 });
      const pointScale = typeof pointsRule.metricAmount === "number" ? pointsRule.metricAmount : 1;
      const pointValue = typeof pointsRule.pointsEarned === "number" ? pointsRule.pointsEarned : 10;
      const direction = gameConfig?.effectType === "negative" ? -1 : (rawKpi.direction ?? 1);
      const metricType = gameConfig?.type === "anchor" ? KPI_TYPES[1] : KPI_TYPES[0];
      const name = rawKpi.name || rawKpi.title || model?.type || "Metric";
      const requiredInputs = rawKpi.requiredInputs || [{ type: "text", value: "none" }];

      const kpiData = {
        name,
        title: name,
        kind,
        direction,
        target: typeof target === "number" ? target : 100,
        pointScale,
        pointValue,
        metricType,
        isDraft: false,
        isSaved: false,
      };

      // Persist full gameConfig so backend and UI show gamification (status, dailyTarget, pointsRule, type, effectType, valueType, etc.)
      const gameConfigForSave = {
        ...(typeof gameConfig === "object" && gameConfig !== null ? gameConfig : {}),
        status: "live",
        dailyTarget: typeof target === "number" ? target : (gameConfig?.dailyTarget ?? 100),
        pointsRule: pointsRule && typeof pointsRule === "object" ? pointsRule : { metricAmount: pointScale, pointsEarned: pointValue },
        type: gameConfig?.type || "progressive",
        effectType: gameConfig?.effectType === "negative" ? "negative" : "positive",
        valueType: gameConfig?.valueType || "monetary",
      };

      // Same pattern as ExploreCustomMetricsTypes: kpiRaw = base + update fields + groupId + requiredInputs + gameConfig
      const kpiRaw = {
        ...baseKpi,
        ...kpiData,
        _id: rawKpi._id || rawKpi.id,
        isCustom: true,
        isDraft: false,
        isSaved: false,
        groupId: targetWsId,
        requiredInputs,
        gameConfig: gameConfigForSave,
        ...(typeof gameConfig?.guideText === "string" ? { guideText: gameConfig.guideText.trim() } : {}),
      };
      applyAdvancedGameSettingsToKpiPayload(kpiRaw, rawKpi, gameConfigForSave);
      if (gameConfigForSave.type === "anchor") {
        applyAnchorGameSettingsToKpiPayload(kpiRaw, gameConfigForSave);
      }
      if (gameConfigForSave.monitorMode) {
        kpiRaw.pointValue = 0;
      }

      try {
        await disableWorkspaceKpi(metric.id);
        await updateWorkspaceKpi(targetWsId, kpiRaw);
        triggerKpiRefresh();
        setGamifyForWorkspaceMetric(null);
        if (rawKpi.isZapier && rawKpi.integrationMeta) {
          navigate(`/zapier-setup?id=${rawKpi.integrationMeta.id}`);
        } else {
          setToast({ message: "The metric is now gamified.", type: "success" });
        }
        setIsSwitchingContext(false);
      } catch (err) {
        setToast({
          message: err?.message || "Failed to update metric for workspace. Please try again.",
          type: "error",
        });
        setIsSwitchingContext(false);
        return;
      }
    },
    [allCompanyKpisRaw, disableWorkspaceKpi, updateWorkspaceKpi, triggerKpiRefresh, navigate]
  );

  const handleSaveGuideText = useCallback(
    async (metric, guideText) => {
      const kpiId = metric?.id || metric?._id;
      if (!kpiId) {
        throw new Error("Could not find KPI to update on the server.");
      }
      const trimmed = typeof guideText === "string" ? guideText.trim() : "";
      await updateKpiGuideText(kpiId, trimmed);
      triggerKpiRefresh();
    },
    [updateKpiGuideText, triggerKpiRefresh],
  );

  const handleSaveDailyGoalText = useCallback(
    async (metric, dailyGoalText) => {
      const kpiId = metric?.id || metric?._id;
      if (!kpiId) {
        throw new Error("Could not find KPI to update on the server.");
      }
      const trimmed = typeof dailyGoalText === "string" ? dailyGoalText.trim() : "";
      await updateKpiDailyGoalText(kpiId, trimmed);
      triggerKpiRefresh();
    },
    [updateKpiDailyGoalText, triggerKpiRefresh],
  );

  const handleReopenWorkspaceSelector = useCallback(() => {
    setSettingsMetric(null);
    const templateMetric = allMetrics.find((m) => m.id === settingsMetric?.id && m.workspaceId === "GLOBAL");
    if (templateMetric) setGamifyForWorkspaceMetric(templateMetric);
  }, [allMetrics, settingsMetric]);

  // Workspaces this template has already been assigned to — sourced from the
  // real database (allCompanyKpisRaw), not the separate localStorage-only
  // `allMetrics` list, which never reflects server-created copies. Includes:
  // (1) any copy created from this template (sourceTemplateId matches), and
  // (2) the template's own workspace, if it's already sitting on a real one
  // (as opposed to the hidden company-level catalog placeholder).
  // Workspaces this metric is already linked to — its own primary groupId
  // (if it's a real workspace, not just the hidden company-level catalog
  // placeholder) plus every workspace in linkedGroupIds. Read straight off
  // the one real document; there's no copy to search for anymore.
  const currentlyAssignedIds = useMemo(() => {
    if (!assigningMetric) return [];
    const template = allCompanyKpisRaw.find((k) => (k._id || k.id) === assigningMetric.id);
    if (!template) return [];
    const templateGroupId = template.groupId?._id || template.groupId;
    const ids = (template.linkedGroupIds || []).map((id) => id?._id || id);
    if (templateGroupId && String(templateGroupId) !== String(template.companyId)) {
      ids.push(templateGroupId);
    }
    return [...new Set(ids.filter(Boolean).map(String))];
  }, [allCompanyKpisRaw, assigningMetric]);

  return (
    <div style={{ ...themeVars, marginTop: "24px" }}>
      {topTab === "dashboards" && (
        <>
          <PageHeader
            headerText="Dashboards"
            subheaderText="Build and share your own views from your company metrics."
          />
          {/* Mobile-only: PageHeader's banner has zero bottom margin below md,
              so this wrapper supplies its own top gap on mobile instead of the
              content sitting flush (overlapping-looking) against the banner —
              same fix as the Metrics tab's Dashboard.jsx wrapper. */}
          <div className="max-w-[1600px] mx-auto pl-[16px] pr-[16px] md:pl-[32px] md:pr-[32px] pt-[16px] md:pt-0 pb-10">
            <DashboardsPanel brandPrimary={brandPrimary || "#1b598e"} />
          </div>
        </>
      )}

      {topTab === "metrics" && (
        <>
      {view === "COMPANY_DASHBOARD" && (
        <Dashboard
          metrics={combinedMetrics}
          allMetrics={combinedAllMetrics}
          columnCount={columnCount}
          onUpdateColumns={handleUpdateColumns}
          onAddIntegrated={() => setView("APP_SELECTION")}
          onAddManual={() => setView("MANUAL_CREATOR")}
          onRemoveMetric={handleRemoveMetric}
          onEditMetric={(m, tab) => { setSettingsMetric(m); setSettingsTab(tab || "appearance"); }}
          onTriggerAssign={(m) => setAssigningMetric(m)}
          onTriggerGamifyForWorkspace={(m) => setGamifyForWorkspaceMetric(m)}
          allWorkspaces={workspacesForAssign}
          integrationApps={allAppsRaw?.length ? allAppsRaw : integrationApps}
          isGlobalView
          userRole={role}
          kpiLoading={kpiLoading}
          companyScores={companyScores}
          togglePeriod={togglePeriod}
          onPeriodChange={setTogglePeriod}
          rawKpis={allCompanyKpisRaw}
          onSaveGuideText={handleSaveGuideText}
          onSaveDailyGoalText={handleSaveDailyGoalText}
        />
      )}

      {view === "APP_SELECTION" && (
        <AppSelector
          apps={appsWithMetrics}
          isLoading={appsLoading}
          onSelect={(app) => setSelectedApp(app)}
          onCancel={() => setView("COMPANY_DASHBOARD")}
        />
      )}

      {view === "MANUAL_CREATOR" && (
        <ManualMetricCreator
          onCancel={() => setView("COMPANY_DASHBOARD")}
          onAdd={async (metricDef, manualConfig) => { await handleConfirmManualMetric(metricDef, manualConfig); setView("COMPANY_DASHBOARD"); }}
        />
      )}
        </>
      )}

      <MetricModal
        app={selectedApp}
        companyKpis={allCompanyKpisRaw}
        onClose={() => { setSelectedApp(null); setView("COMPANY_DASHBOARD"); }}
        onAdd={handleConfirmMetric}
        onRemoveMetric={handleRemoveAppMetric}
      />

      {assigningMetric && (
        <AssignModal
          metric={assigningMetric}
          workspaces={workspacesForAssign}
          onClose={() => setAssigningMetric(null)}
          onAssign={handleAssignMetric}
          initiallyAssignedIds={currentlyAssignedIds}
        />
      )}

      {gamifyForWorkspaceMetric && (
        <GamifyWorkspaceModal
          metric={gamifyForWorkspaceMetric}
          workspaces={workspacesForAssign}
          allMetrics={allMetrics}
          onClose={() => setGamifyForWorkspaceMetric(null)}
          onContinue={(wsId, gameConfig) => handleGamifyForWorkspace(gamifyForWorkspaceMetric, wsId, gameConfig)}
        />
      )}

      {settingsMetric && (
        <MetricSettingsModal
          metric={settingsMetric}
          allMetrics={combinedAllMetrics}
          allWorkspaces={companyWorkspaces}
          isGlobalView={contextMode === "GLOBAL"}
          activeWorkspace={activeWorkspace}
          initialTab={settingsTab}
          integrationApps={allAppsRaw?.length ? allAppsRaw : integrationApps}
          companyScoresByDay={companyScoresByDay}
          onClose={() => setSettingsMetric(null)}
          onReopenWorkspaceSelector={handleReopenWorkspaceSelector}
          onSave={(metricId, visual, game, shouldClose = true) => {
            handleSaveMetricFromSettings(metricId, visual, game, shouldClose);
          }}
        />
      )}

      {isSwitchingContext && (
        <div className="fixed inset-0 z-[400] bg-white flex items-center justify-center">
          <BrandedTransition message={`Switching to ${activeWorkspace?.name ?? "workspace"}...`} />
        </div>
      )}

      {toast && (
        <div className="fixed bottom-8 left-1/2 -translate-x-1/2 z-[300] animate-in fade-in slide-in-from-bottom-4 duration-300">
          <div className="bg-gray-900 text-white px-6 py-3.5 rounded-2xl shadow-2xl flex items-center gap-4 min-w-[320px]">
            <div className="w-6 h-6 bg-emerald-500 rounded-full flex items-center justify-center text-white text-[10px]">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="4" className="w-3 h-3">
                <polyline points="20 6 9 17 4 12" />
              </svg>
            </div>
            <p className="text-sm font-semibold flex-grow">{toast.message}</p>
            <button type="button" onClick={() => setToast(null)} className="text-[10px] font-bold text-gray-400 hover:text-white uppercase tracking-widest">OK</button>
          </div>
        </div>
      )}
    </div>
  );
}

export default CompanyMetricsPage;
